/**
 * US-092 — mirror of assisty-bot's
 * `LandedCostCalculationService.deriveLandedCostFromUnitPrice`. Keep both in
 * sync, the same way `poCostResolver.ts` mirrors `CurrencyConversionService`.
 *
 * The receive dialog asks for the SUPPLIER's clean unit price. Shopify is sent a
 * LANDED cost, so the dialog shows the merchant what that will be before they
 * confirm. If this drifts from the backend, the hint lies about what the server
 * is about to write — which is worse than showing nothing.
 */

/** The subset of `PurchaseOrderItem.extra.landed_cost_components` this needs. */
export interface LandedCostComponents {
  method?: string;
  discount_factor?: number;
  landed_cost_ratio?: number;
  discounted_subtotal?: number;
  shipping_cost?: number;
  adjustment_amount?: number;
  shipping_per_unit?: number;
  adjustment_per_unit?: number;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function round6(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000000) / 1000000;
}

function finiteOr(value: unknown, fallback: number): number {
  // null/undefined must mean "absent", not 0. These components come from a
  // JSONB blob fed by nullable DECIMAL columns, and Number(null) is 0 — which
  // IS finite, so a bare isFinite check would silently accept it and zero out
  // the cost (a null discount_factor multiplied the whole price by 0).
  if (value == null || value === '') { return fallback; }
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * @param unitPriceStore Clean unit price already converted to STORE currency.
 * @param components     The PO line's persisted `extra.landed_cost_components`.
 * @returns Landed cost in store currency, rounded to 2dp.
 */
export function deriveLandedCostFromUnitPrice(
  unitPriceStore: number | null | undefined,
  components: LandedCostComponents | null | undefined,
): number {
  const price = Number(unitPriceStore);
  if (!Number.isFinite(price)) { return 0; }

  // Older POs predate landed cost, or were never recalculated — nothing to
  // allocate, so the clean price is the cost we can defend.
  if (!components) { return round2(price); }

  const discountFactor = finiteOr(components.discount_factor, 1);
  const effectiveUnitPrice = round2(price * discountFactor);

  if (components.method === 'proportional') {
    const shippingPerUnit = finiteOr(components.shipping_per_unit, 0);
    const adjustmentPerUnit = finiteOr(components.adjustment_per_unit, 0);
    return round2(effectiveUnitPrice + shippingPerUnit + adjustmentPerUnit);
  }

  // `components.landed_cost_ratio` is the DISPLAY ratio — the backend stores
  // round2(ratio) there while multiplying by the round6 value. Reading it back
  // loses precision (1.114286 reads as 1.11, pricing a 10.00 line at 9.71 rather
  // than 9.75), so recompute from the raw inputs the components also carry, and
  // keep the stored value only as a fallback for rows predating those fields.
  // Same null trap: Number(null) is 0 and finite, which would take the
  // recompute branch with a zero subtotal instead of the stored-ratio fallback.
  const discountedSubtotal = components.discounted_subtotal == null
    ? NaN
    : Number(components.discounted_subtotal);
  let landedCostRatio: number;
  if (Number.isFinite(discountedSubtotal)) {
    const shippingCost = finiteOr(components.shipping_cost, 0);
    const adjustmentAmount = finiteOr(components.adjustment_amount, 0);
    landedCostRatio = discountedSubtotal > 0
      ? round6((discountedSubtotal + adjustmentAmount + shippingCost) / discountedSubtotal)
      : 1.0;
  } else {
    landedCostRatio = finiteOr(components.landed_cost_ratio, 1);
  }

  return round2(effectiveUnitPrice * landedCostRatio);
}
